import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { LogoIcon } from '@components/ui';

// ── Types ─────────────────────────────────────────────────────────────────────

type ServiceStatus = 'operational' | 'degraded' | 'outage' | 'maintenance';
type DayStatus = ServiceStatus | 'no-data';

interface DayHistory {
  date: string;
  status: DayStatus;
}

interface Service {
  id: string;
  nameKey: string;
  currentStatus: ServiceStatus;
  uptimePct: number;
  history: DayHistory[];
}

interface Incident {
  id: string;
  date: string;
  title: string;
  incidentStatus: 'resolved' | 'investigating' | 'monitoring';
  affected: string;
  duration: string;
}

// ── Mock data helpers ─────────────────────────────────────────────────────────

function offsetDate(daysAgo: number): string {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  return d.toISOString().slice(0, 10);
}

function buildHistory(
  outages: Array<{ offset: number; status: Exclude<DayStatus, 'no-data' | 'operational'> }>,
): DayHistory[] {
  const map = new Map(outages.map(({ offset, status }) => [offset, status]));
  return Array.from({ length: 30 }, (_, i) => ({
    date: offsetDate(29 - i),
    status: (map.get(29 - i) ?? 'operational') as DayStatus,
  }));
}

// ── Mock data ─────────────────────────────────────────────────────────────────

const MOCK_SERVICES: Service[] = [
  {
    id: 'api',
    nameKey: 'status.services.api',
    currentStatus: 'operational',
    uptimePct: 99.7,
    history: buildHistory([{ offset: 17, status: 'outage' }]),
  },
  {
    id: 'calendar',
    nameKey: 'status.services.calendar',
    currentStatus: 'operational',
    uptimePct: 100,
    history: buildHistory([]),
  },
  {
    id: 'notifications',
    nameKey: 'status.services.notifications',
    currentStatus: 'operational',
    uptimePct: 99.3,
    history: buildHistory([
      { offset: 10, status: 'degraded' },
      { offset: 11, status: 'degraded' },
    ]),
  },
  {
    id: 'invoicing',
    nameKey: 'status.services.invoicing',
    currentStatus: 'operational',
    uptimePct: 100,
    history: buildHistory([]),
  },
  {
    id: 'records',
    nameKey: 'status.services.records',
    currentStatus: 'operational',
    uptimePct: 100,
    history: buildHistory([]),
  },
  {
    id: 'auth',
    nameKey: 'status.services.auth',
    currentStatus: 'operational',
    uptimePct: 100,
    history: buildHistory([]),
  },
];

const MOCK_INCIDENTS: Incident[] = [
  {
    id: 'inc-2',
    date: offsetDate(10),
    title: 'Notification delivery delays',
    incidentStatus: 'resolved',
    affected: 'Notifications',
    duration: '38h',
  },
  {
    id: 'inc-1',
    date: offsetDate(17),
    title: 'API elevated error rate',
    incidentStatus: 'resolved',
    affected: 'API',
    duration: '2h 14m',
  },
];

// ── Style helpers ─────────────────────────────────────────────────────────────

function dayBarClass(status: DayStatus): string {
  switch (status) {
    case 'operational':  return 'bg-success';
    case 'degraded':     return 'bg-warning';
    case 'outage':       return 'bg-error';
    case 'maintenance':  return 'bg-info';
    default:             return 'bg-muted';
  }
}

function serviceBadgeClass(status: ServiceStatus): string {
  switch (status) {
    case 'operational':  return 'bg-success/10 text-success';
    case 'degraded':     return 'bg-warning/10 text-warning';
    case 'outage':       return 'bg-error/10 text-error';
    case 'maintenance':  return 'bg-info/10 text-info';
  }
}

function incidentBadgeClass(status: Incident['incidentStatus']): string {
  switch (status) {
    case 'resolved':     return 'bg-success/10 text-success';
    case 'monitoring':   return 'bg-warning/10 text-warning';
    case 'investigating': return 'bg-error/10 text-error';
  }
}

function overallStatus(services: Service[]): ServiceStatus {
  if (services.some(s => s.currentStatus === 'outage'))       return 'outage';
  if (services.some(s => s.currentStatus === 'degraded'))     return 'degraded';
  if (services.some(s => s.currentStatus === 'maintenance'))  return 'maintenance';
  return 'operational';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function OverallBanner({ status }: { status: ServiceStatus }) {
  const { t } = useTranslation();

  const bannerClass = {
    operational: 'border-success/30 bg-success/5',
    degraded:    'border-warning/30 bg-warning/5',
    outage:      'border-error/30 bg-error/5',
    maintenance: 'border-info/30 bg-info/5',
  }[status];

  const iconClass = {
    operational: 'text-success',
    degraded:    'text-warning',
    outage:      'text-error',
    maintenance: 'text-info',
  }[status];

  const label = t(`status.overall.${status}`);

  return (
    <div className={`flex items-center gap-3 rounded-lg border px-5 py-4 ${bannerClass}`}>
      {status === 'operational' ? (
        <svg className={`h-5 w-5 shrink-0 ${iconClass}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
        </svg>
      ) : (
        <svg className={`h-5 w-5 shrink-0 ${iconClass}`} viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 5a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 5zm0 9a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
        </svg>
      )}
      <span className={`text-base font-semibold ${iconClass}`}>{label}</span>
    </div>
  );
}

interface ServiceRowProps {
  service: Service;
}

function ServiceRow({ service }: ServiceRowProps) {
  const { t, i18n } = useTranslation();

  return (
    <div className="py-4">
      <div className="mb-2 flex items-center justify-between gap-4">
        <span className="text-sm font-medium text-foreground">{t(service.nameKey)}</span>
        <div className="flex items-center gap-3">
          <span className="hidden text-xs text-muted-foreground sm:inline">
            {service.uptimePct.toFixed(1)}% {t('status.thirtyDayUptime')}
          </span>
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${serviceBadgeClass(service.currentStatus)}`}>
            {t(`status.badges.${service.currentStatus}`)}
          </span>
        </div>
      </div>

      {/* 30-day uptime bar */}
      <div className="flex gap-px" role="img" aria-label={t('status.thirtyDayUptime')}>
        {service.history.map((day) => {
          const label = `${new Date(day.date).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric' })} — ${t(`status.dayStatus.${day.status}`)}`;
          return (
            <div
              key={day.date}
              title={label}
              aria-label={label}
              className={`h-8 flex-1 rounded-xs transition-opacity hover:opacity-70 ${dayBarClass(day.status)}`}
            />
          );
        })}
      </div>

      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>{t('status.thirtyDaysAgo')}</span>
        <span className="sm:hidden">{service.uptimePct.toFixed(1)}%</span>
        <span>{t('status.today')}</span>
      </div>
    </div>
  );
}

interface IncidentRowProps {
  incident: Incident;
}

function IncidentRow({ incident }: IncidentRowProps) {
  const { t } = useTranslation();

  return (
    <div className="border-b border-border py-4 last:border-0">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-foreground">{incident.title}</p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {formatDate(incident.date)} · {t('status.incidents.affected', { services: incident.affected })} · {t('status.incidents.duration', { duration: incident.duration })}
          </p>
        </div>
        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${incidentBadgeClass(incident.incidentStatus)}`}>
          {t(`status.incidents.${incident.incidentStatus}`)}
        </span>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function StatusPage() {
  const { t } = useTranslation();
  const current = overallStatus(MOCK_SERVICES);
  const now = new Date().toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });

  return (
    <div className="flex min-h-screen flex-col bg-background">

      {/* Fixed nav — matches HomePage brand */}
      <header className="fixed inset-x-0 top-0 z-50 h-[60px] border-b border-border bg-background/[.92] backdrop-blur-md">
        <div className="mx-auto flex h-full max-w-3xl items-center px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-[10px] no-underline">
            <div className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[7px] bg-brand">
              <LogoIcon size={18} />
            </div>
            <span className="text-[17px] font-semibold tracking-[-0.03em] text-foreground">calendo</span>
          </Link>
        </div>
      </header>

      {/* Spacer so content clears the fixed header */}
      <div className="h-[60px]" aria-hidden="true" />

      {/* Main content */}
      <main className="mx-auto w-full max-w-3xl flex-1 px-4 py-10 sm:px-6">
        <div className="mb-8">
          <h1 className="font-display text-3xl font-semibold text-foreground">{t('status.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('status.subtitle')}</p>
        </div>

        <OverallBanner status={current} />

        {/* Services */}
        <section className="mt-10">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('status.services.title')}
          </h2>
          <div className="mt-3 divide-y divide-border rounded-lg border border-border bg-surface px-4 sm:px-5">
            {MOCK_SERVICES.map((service) => (
              <ServiceRow key={service.id} service={service} />
            ))}
          </div>
          {/* Legend */}
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground">
            {(['operational', 'degraded', 'outage', 'maintenance'] as const).map((s) => (
              <span key={s} className="flex items-center gap-1.5">
                <span className={`inline-block h-2.5 w-2.5 rounded-xs ${dayBarClass(s)}`} />
                {t(`status.badges.${s}`)}
              </span>
            ))}
          </div>
        </section>

        {/* Past incidents */}
        <section className="mt-10">
          <h2 className="mb-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t('status.incidents.title')}
          </h2>
          <div className="mt-3 rounded-lg border border-border bg-surface px-4 sm:px-5">
            {MOCK_INCIDENTS.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">
                {t('status.incidents.noIncidents')}
              </p>
            ) : (
              MOCK_INCIDENTS.map((incident) => (
                <IncidentRow key={incident.id} incident={incident} />
              ))
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
          <p className="text-xs text-muted-foreground">
            {t('status.lastUpdated')}: {now}
          </p>
          <Link to="/" className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            calendo.ro
          </Link>
        </div>
      </footer>

    </div>
  );
}
